import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import BackgroundProcessesButton from './BackgroundProcessesButton';
import Toast from '../Common/Toast';
import { authService } from '../../services/authService';
import { useKardexRecalculateNotifications } from '../../hooks/useKardexRecalculateNotifications';
import { useAuditNotifications } from '../../hooks/useAuditNotifications';
import AuditNotificationsButton from '../Audit/AuditNotificationsButton';
import { isLimitedReadOnlyUser } from '../../hooks/useCanMutate';
import {
  getLimitedUserScope,
  getAccountantHomePath,
  isAccountantUser,
  isPathAllowedForAccountant,
  isPathAllowedForLimitedUser,
  isComprasFrontendPath,
  canLimitedUserMutateWarehouseGuides,
  canLimitedUserMutateKardex,
  canLimitedUserMutateSunatMappings,
} from '../../utils/limitedUserAccess';

/** Solo bloquea pantallas de alta; /edit se usa para consultar (guardar bloqueado por API). */
const WRITE_PATH_PATTERN = /\/(create|nuevo|recalculate)(\/|$)/i;

const COMPRAS_HOME = '/purchase-orders';

const Layout = () => {
  const location = useLocation();
  const [sessionUser, setSessionUser] = useState(() => authService.getCurrentUser());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toast: kardexToast, closeToast: closeKardexToast } = useKardexRecalculateNotifications();
  const { toast: auditToast, closeToast: closeAuditToast, unreadCount, isRecipient } = useAuditNotifications();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    if (!authService.isAuthenticated()) return undefined;
    authService
      .syncUserFromApi()
      .then((u) => {
        if (!cancelled) setSessionUser(u);
      })
      .catch(() => {
        if (!cancelled) setSessionUser(authService.getCurrentUser());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const user = sessionUser;
  const isAccountant = isAccountantUser(user);
  const isLimited = isLimitedReadOnlyUser(user);
  const onCompras = isComprasFrontendPath(location.pathname);
  const onWarehouseGuides = canLimitedUserMutateWarehouseGuides(location.pathname, user);
  const onKardexMutate = canLimitedUserMutateKardex(location.pathname, user);
  const onSunatMutate = canLimitedUserMutateSunatMappings(location.pathname, user);
  const readOnly = isLimited && !onCompras && !onWarehouseGuides && !onKardexMutate && !onSunatMutate;

  if (isAccountant && !isPathAllowedForAccountant(location.pathname)) {
    return <Navigate to={getAccountantHomePath()} replace />;
  }

  if (isAccountant && /^\/dashboard(\/|$)/.test(location.pathname || '')) {
    return <Navigate to={getAccountantHomePath()} replace />;
  }

  if (isLimited && WRITE_PATH_PATTERN.test(location.pathname) && !onCompras && !onWarehouseGuides && !onKardexMutate && !onSunatMutate) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLimited && !isPathAllowedForLimitedUser(location.pathname, user)) {
    return <Navigate to={COMPRAS_HOME} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Cerrar menú"
          className="xl:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <header className="xl:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 shadow-sm">
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span className="text-base font-semibold text-teal-600 truncate">Contify Logistic</span>
        {!isLimited ? (
          <div className="ml-auto flex items-center gap-2">
            <AuditNotificationsButton compact unreadCount={unreadCount} isRecipient={isRecipient} />
            <BackgroundProcessesButton compact />
          </div>
        ) : null}
      </header>

      {!isLimited ? (
        <div className="hidden xl:flex fixed top-4 right-5 z-40 items-center gap-2">
          <AuditNotificationsButton unreadCount={unreadCount} isRecipient={isRecipient} />
          <BackgroundProcessesButton />
        </div>
      ) : null}

      <Sidebar mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 max-w-full overflow-x-hidden pt-14 xl:pt-0 xl:ml-64">
        <Outlet context={{ readOnly }} />
      </main>

      <Toast
        isOpen={kardexToast.isOpen}
        message={kardexToast.message}
        type={kardexToast.type}
        onClose={closeKardexToast}
        duration={8000}
      />
      <Toast
        isOpen={auditToast.isOpen}
        message={auditToast.message}
        type={auditToast.type}
        onClose={closeAuditToast}
        duration={8000}
      />
    </div>
  );
};

export default Layout;
